import React, { useState, useContext } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import {
  getCashTransactionClient,
  CloseTillRequest,
  CashReconciliationDto,
  DenominationBreakdownDto,
  formatCashAmount,
} from '../../../app/services/api/cashTransactions';
import DenominationBreakdown from '../components/DenominationBreakdown';
import useLogError from '../../../hooks/useLogError';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';

interface CloseTillModalProps {
  open: boolean;
  reconciliation: CashReconciliationDto;
  onClose: () => void;
  onSuccess: () => void;
}

const CloseTillModal: React.FC<CloseTillModalProps> = ({
  open,
  reconciliation,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('cashReconciliation');
  const { selectedStore } = useContext(UserContext);
  const { logError } = useLogError();
  const cashClient = getCashTransactionClient();

  const [loading, setLoading] = useState(false);
  const [countMethod, setCountMethod] = useState<'QuickTotal' | 'DenominationBreakdown'>('QuickTotal');
  const [declaredTotal, setDeclaredTotal] = useState<string>('');
  const [breakdown, setBreakdown] = useState<DenominationBreakdownDto | undefined>(undefined);
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [varianceWarning, setVarianceWarning] = useState<string>('');

  const expectedClosing = reconciliation.expectedClosingBalance;
  const declaredAmount = countMethod === 'QuickTotal'
    ? parseFloat(declaredTotal) || 0
    : breakdown?.grandTotal || 0;
  const variance = declaredAmount - expectedClosing;
  const hasVariance = Math.abs(variance) > 0.01;
  const variancePercentage = expectedClosing !== 0
    ? (variance / expectedClosing) * 100
    : 0;

  const handleBreakdownChange = (newBreakdown: DenominationBreakdownDto) => {
    setBreakdown(newBreakdown);
    setDeclaredTotal(newBreakdown.grandTotal.toFixed(2));
  };

  const handleSubmit = async () => {
    setError('');
    setVarianceWarning('');

    // Validation
    if (isNaN(declaredAmount) || declaredAmount < 0) {
      setError(t('errors.invalidDeclaredAmount', 'Please enter a valid cash amount'));
      return;
    }

    // Check variance threshold
    const zone = await cashClient.getCashZoneById(reconciliation.zoneId);
    if (Math.abs(variance) > zone.varianceThresholdAmount) {
      setVarianceWarning(
        t(
          'closeTill.varianceThresholdExceeded',
          `Variance of ${formatCashAmount(Math.abs(variance), 'GBP')} exceeds threshold of ${formatCashAmount(zone.varianceThresholdAmount, 'GBP')}. Manager approval will be required.`,
        ),
      );
    }

    try {
      setLoading(true);

      const request: CloseTillRequest = {
        reconciliationId: reconciliation.id,
        declaredClosingBalance: declaredAmount,
        denominationBreakdown: countMethod === 'DenominationBreakdown' ? breakdown : undefined,
        countMethod,
        notes: notes.trim() || undefined,
      };

      await cashClient.closeTill(request);

      setShowSuccess(true);
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 1500);

    } catch (err) {
      logError(err);
      setError(t('errors.closeTillFailed', 'Failed to close till. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDeclaredTotal('');
    setBreakdown(undefined);
    setNotes('');
    setError('');
    setVarianceWarning('');
    setCountMethod('QuickTotal');
    onClose();
  };

  return (
    <>
      <Dialog open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth>
        <DialogTitle>{t('closeTill.title', 'Close Till & Declare Cash')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3}
            sx={{ mt: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {varianceWarning && <Alert severity="warning">{varianceWarning}</Alert>}

            {/* Till Info */}
            <Paper variant="outlined"
              sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="textMD"
                fontWeight={600}
                gutterBottom>
                {t('closeTill.tillInfo', 'Till Information')}
              </Typography>
              <Stack spacing={1}>
                <Box display="flex"
                  justifyContent="space-between">
                  <Typography variant="textSM"
                    color="text.secondary">
                    {t('closeTill.tillNumber', 'Till Number')}:
                  </Typography>
                  <Typography variant="textSM"
                    fontWeight={600}>
                    {reconciliation.tillNumber}
                  </Typography>
                </Box>
                <Box display="flex"
                  justifyContent="space-between">
                  <Typography variant="textSM"
                    color="text.secondary">
                    {t('closeTill.openedBy', 'Opened By')}:
                  </Typography>
                  <Typography variant="textSM"
                    fontWeight={600}>
                    {reconciliation.openedByUsername}
                  </Typography>
                </Box>
                <Box display="flex"
                  justifyContent="space-between">
                  <Typography variant="textSM"
                    color="text.secondary">
                    {t('closeTill.openingBalance', 'Opening Balance')}:
                  </Typography>
                  <Typography variant="textSM"
                    fontWeight={600}>
                    {formatCashAmount(reconciliation.openingBalance, 'GBP')}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex"
                  justifyContent="space-between">
                  <Typography variant="textSM"
                    fontWeight={700}>
                    {t('closeTill.expectedClosing', 'Expected Closing')}:
                  </Typography>
                  <Typography variant="textSM"
                    fontWeight={700}
                    color="primary">
                    {formatCashAmount(expectedClosing, 'GBP')}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* Cash Declaration */}
            <Box>
              <Typography variant="textLG"
                fontWeight={600}
                gutterBottom>
                {t('closeTill.declareCash', 'Declare Cash in Till')}
              </Typography>

              <DenominationBreakdown
                value={breakdown}
                onChange={handleBreakdownChange}
                expectedTotal={expectedClosing}
                currencyCode="GBP"
                showQuickTotalToggle={true}
                disabled={loading}
              />
            </Box>

            {/* Variance Display */}
            {hasVariance && (
              <Alert
                severity={Math.abs(variance) > 10 ? 'error' : 'warning'}
                sx={{ mt: 2 }}
              >
                <Typography variant="textMD"
                  fontWeight={600}>
                  {t('closeTill.varianceDetected', 'Variance Detected')}
                </Typography>
                <Typography variant="textSM">
                  {variance > 0
                    ? t('closeTill.overExpected', 'Till is over by {{amount}}', { amount: formatCashAmount(variance, 'GBP') })
                    : t('closeTill.underExpected', 'Till is under by {{amount}}', { amount: formatCashAmount(Math.abs(variance), 'GBP') })
                  }
                  {' '}({Math.abs(variancePercentage).toFixed(2)}%)
                </Typography>
              </Alert>
            )}

            {/* Notes */}
            <TextField
              label={t('closeTill.notes', 'Notes (Required if variance)')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              fullWidth
              multiline
              rows={3}
              required={hasVariance}
              placeholder={t(
                'closeTill.notesPlaceholder',
                'Explain any variance or provide additional information...',
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}
            disabled={loading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="primary"
            disabled={loading || (hasVariance && !notes.trim())}
            startIcon={loading && <CircularProgress size={20} />}
            color={hasVariance ? 'warning' : 'primary'}
          >
            {hasVariance
              ? t('closeTill.submitWithVariance', 'Submit with Variance')
              : t('closeTill.closeTill', 'Close Till')
            }
          </Button>
        </DialogActions>
      </Dialog>

      <MessagesSnackbar
        open={showSuccess}
        message={t('closeTill.success', 'Till closed successfully')}
        severity="success"
        duration={3000}
        onClose={() => setShowSuccess(false)}
      />
    </>
  );
};

export default CloseTillModal;
